import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Plus,
  RefreshCw,
  User,
  FileText,
} from "lucide-react";

const LEAVE_TYPE_CONFIG = {
  annual: { label: "Annual Leave", color: "bg-blue-100 text-blue-800", days: 24 },
  sick: { label: "Sick Leave", color: "bg-yellow-100 text-yellow-800", days: 26 },
  maternity: { label: "Maternity Leave", color: "bg-pink-100 text-pink-800", days: 98 },
  paternity: { label: "Paternity Leave", color: "bg-cyan-100 text-cyan-800", days: 5 },
  compassionate: { label: "Compassionate Leave", color: "bg-purple-100 text-purple-800", days: 7 },
  unpaid: { label: "Unpaid Leave", color: "bg-gray-100 text-gray-800", days: 0 },
  study: { label: "Study Leave", color: "bg-green-100 text-green-800", days: 10 },
};

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  approved: { label: "Approved", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800", icon: XCircle },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-500", icon: AlertCircle },
};

const LeaveManagement = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("requests");
  const [showForm, setShowForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState({ status: "" });

  const canApprove = ["admin", "accounts", "people_culture"].includes(user?.role);

  const [formData, setFormData] = useState({
    employee_id: "",
    leave_type: "annual",
    start_date: "",
    end_date: "",
    days_requested: 0,
    reason: "",
  });

  const [reviewNotes, setReviewNotes] = useState("");

  const fetchRequests = async () => {
    try {
      let url = `${API}/leave/requests`;
      if (filter.status) url += `?status=${filter.status}`;
      const response = await axios.get(url);
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error("Leave requests error:", error);
      if (error.response?.status !== 403) {
        toast.error("Failed to load leave requests");
      }
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/payroll/employees`);
      const data = response.data;
      setEmployees(Array.isArray(data) ? data : (data?.employees || []));
    } catch (error) {
      console.error("Employees fetch error:", error);
      if (error.response?.status === 403) {
        toast.error("You don't have permission to view employees");
      }
    }
  };

  const fetchSummary = async () => {
    if (!canApprove) return;
    try {
      const response = await axios.get(`${API}/leave/summary`);
      setSummary(response.data);
    } catch (error) {
      // Non-critical
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRequests(), fetchEmployees(), fetchSummary()]);
      setLoading(false);
    };
    loadData();
  }, [filter]);

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const handleDateChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    if (newData.start_date && newData.end_date) {
      newData.days_requested = calculateDays(newData.start_date, newData.end_date);
    }
    setFormData(newData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id) {
      toast.error("Please select an employee");
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      toast.error("Please select dates");
      return;
    }

    try {
      await axios.post(`${API}/leave/requests`, formData);
      toast.success("Leave request submitted");
      setShowForm(false);
      setFormData({
        employee_id: "",
        leave_type: "annual",
        start_date: "",
        end_date: "",
        days_requested: 0,
        reason: "",
      });
      fetchRequests();
      fetchSummary();
    } catch (error) {
      console.error("Leave submit error:", error);
      console.error("Error response:", error.response);
      console.error("Payload sent:", formData);
      
      let msg;
      if (!error.response) {
        // Network error / CORS / server unreachable
        msg = `Network error: ${error.message}. Check connection or contact support.`;
      } else {
        const status = error.response.status;
        const detail = error.response.data?.detail;
        if (typeof detail === "string") {
          msg = `${status}: ${detail}`;
        } else if (Array.isArray(detail) && detail.length > 0) {
          msg = detail.map(d => `${d.loc?.slice(-1)[0] || "field"}: ${d.msg}`).join("; ");
        } else {
          msg = `Error ${status}: ${JSON.stringify(error.response.data).slice(0, 200)}`;
        }
      }
      toast.error(msg, { duration: 8000 });
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      await axios.put(`${API}/leave/requests/${leaveId}/approve`, { review_notes: reviewNotes });
      toast.success("Leave request approved");
      setSelectedRequest(null);
      setReviewNotes("");
      fetchRequests();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const handleReject = async (leaveId) => {
    if (!reviewNotes.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    try {
      await axios.put(`${API}/leave/requests/${leaveId}/reject`, { review_notes: reviewNotes });
      toast.success("Leave request rejected");
      setSelectedRequest(null);
      setReviewNotes("");
      fetchRequests();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leave-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Leave Management</h1>
          <p className="text-muted-foreground">Manage employee leave requests</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="new-leave-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Request
        </Button>
      </div>

      {/* Summary Cards */}
      {canApprove && summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{summary.pending}</div>
              <p className="text-sm text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{summary.approved}</div>
              <p className="text-sm text-muted-foreground">Approved</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-600">{summary.rejected}</div>
              <p className="text-sm text-muted-foreground">Rejected</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{summary.year}</div>
              <p className="text-sm text-muted-foreground">Current Year</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-4">
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="px-3 py-2 border rounded-md text-sm"
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <Button variant="outline" size="sm" onClick={() => { fetchRequests(); fetchSummary(); }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {requests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No leave requests</h3>
              <p className="text-muted-foreground">Click "New Request" to submit a leave request</p>
            </CardContent>
          </Card>
        ) : (
          requests.map((req) => {
            const typeConfig = LEAVE_TYPE_CONFIG[req.leave_type] || LEAVE_TYPE_CONFIG.annual;
            const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusConfig.icon;

            return (
              <Card key={req.leave_id} data-testid={`leave-${req.leave_id}`}>
                <CardContent className="pt-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeConfig.color}`}>
                          {typeConfig.label}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{req.employee_name}</span>
                        <span className="text-sm text-muted-foreground">({req.employee_code})</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{formatDate(req.start_date)} - {formatDate(req.end_date)}</span>
                        <span className="font-medium">{req.days_requested} days</span>
                      </div>
                      {req.reason && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-1">{req.reason}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {canApprove && req.status === "pending" && (
                        <>
                          <Button size="sm" onClick={() => setSelectedRequest({ ...req, action: "review" })}>
                            Review
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* New Request Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>New Leave Request</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Employee *</label>
                  <select
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    required
                  >
                    <option value="">Select employee</option>
                    {employees.map((emp) => (
                      <option key={emp.employee_id} value={emp.employee_id}>
                        {emp.name} ({emp.code})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Leave Type *</label>
                  <select
                    value={formData.leave_type}
                    onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    {Object.entries(LEAVE_TYPE_CONFIG).map(([key, { label, days }]) => (
                      <option key={key} value={key}>{label} ({days} days/year)</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Date *</label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => handleDateChange("start_date", e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">End Date *</label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => handleDateChange("end_date", e.target.value)}
                      min={formData.start_date}
                      required
                    />
                  </div>
                </div>

                {formData.days_requested > 0 && (
                  <p className="text-sm font-medium">Total Days: {formData.days_requested}</p>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">Reason *</label>
                  <Textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="Reason for leave"
                    rows={3}
                    required
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1">
                    Submit Request
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Review Modal */}
      {selectedRequest?.action === "review" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Review Leave Request</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-md space-y-2">
                <p><strong>Employee:</strong> {selectedRequest.employee_name}</p>
                <p><strong>Type:</strong> {LEAVE_TYPE_CONFIG[selectedRequest.leave_type]?.label}</p>
                <p><strong>Dates:</strong> {formatDate(selectedRequest.start_date)} - {formatDate(selectedRequest.end_date)}</p>
                <p><strong>Days:</strong> {selectedRequest.days_requested}</p>
                <p><strong>Reason:</strong> {selectedRequest.reason}</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Review Notes</label>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add notes (required for rejection)"
                  rows={3}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => { setSelectedRequest(null); setReviewNotes(""); }} className="flex-1">
                  Cancel
                </Button>
                <Button variant="destructive" onClick={() => handleReject(selectedRequest.leave_id)} className="flex-1">
                  Reject
                </Button>
                <Button onClick={() => handleApprove(selectedRequest.leave_id)} className="flex-1 bg-green-600 hover:bg-green-700">
                  Approve
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;
